
import React, { createContext, useContext, useEffect, useState } from 'react';

type RegionCode = 'GB' | 'US' | 'EU' | 'GLOBAL';

interface RegionContextType {
    region: RegionCode;
    isLoading: boolean;
    userIP: string | null;
    setRegion: (region: RegionCode) => void;
}

const RegionContext = createContext<RegionContextType>({
    region: 'GLOBAL',
    isLoading: true,
    userIP: null,
    setRegion: () => { }, // Default no-op function
});

export const useRegion = () => useContext(RegionContext);

export const RegionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [region, setRegion] = useState<RegionCode>('GLOBAL');
    const [isLoading, setIsLoading] = useState(true);
    const [userIP, setUserIP] = useState<string | null>(null);

    useEffect(() => {
        const fetchRegion = async () => {
            try {
                // Allow manual override via URL query param for testing
                const urlParams = new URLSearchParams(window.location.search);
                const regionParam = urlParams.get('region');

                if (regionParam === 'GB') {
                    console.log("Region forced to GB via URL");
                    setRegion('GB');
                    sessionStorage.setItem('user_region', 'GB');
                    setIsLoading(false);
                    return;
                }

                // Check session storage first to avoid repeated API calls
                const cachedRegion = sessionStorage.getItem('user_region');
                if (cachedRegion) {
                    console.log("Region loaded from session:", cachedRegion);
                    setRegion(cachedRegion as RegionCode);
                    setIsLoading(false);
                    return;
                }

                console.log("Fetching region from ipapi.co...");
                const response = await fetch('https://ipapi.co/json/');
                const data = await response.json();
                console.log("Region API response:", data);

                setUserIP(data.ip);

                let detectedRegion: RegionCode = 'GLOBAL';
                if (data.country_code === 'GB') {
                    detectedRegion = 'GB';
                } else if (data.country_code === 'US') {
                    detectedRegion = 'US';
                } else if (['FR', 'DE', 'IT', 'ES', 'NL'].includes(data.country_code)) {
                    // Simple check for major EU countries, can be expanded
                    detectedRegion = 'EU';
                }

                console.log("Detected Region:", detectedRegion);
                setRegion(detectedRegion);
                sessionStorage.setItem('user_region', detectedRegion);
            } catch (error) {
                console.error('Failed to detect region:', error);
                // Default to GLOBAL on error
            } finally {
                setIsLoading(false);
            }
        };

        fetchRegion();
    }, []);

    return (
        <RegionContext.Provider value={{ region, isLoading, userIP, setRegion }}>
            {children}
        </RegionContext.Provider>
    );
};


